import { collection, query, where, getDocs, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { generateTimeSlots } from '@/lib/appointments';
import { toast } from 'sonner';
import {
  appointmentCustomerFields,
  type AppointmentCustomerSource,
} from '@/lib/scheduling/appointmentCustomer';

export async function getCustomersWithoutAppointments() {
  const customersQuery = query(
    collection(db, 'users'),
    where('role', '==', 'customer')
  );
  const snapshot = await getDocs(customersQuery);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function getActiveCoaches() {
  const coachesQuery = query(
    collection(db, 'users'),
    where('role', '==', 'coach')
  );
  const snapshot = await getDocs(coachesQuery);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function getCoachAvailabilities(coachId: string) {
  const availabilitiesQuery = query(
    collection(db, 'availabilities'),
    where('coachId', '==', coachId),
    where('status', '==', 'approved')
  );
  const snapshot = await getDocs(availabilitiesQuery);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function checkExistingAppointments(customerId: string, date: Date) {
  const appointmentsQuery = query(
    collection(db, 'appointments'),
    where('customerId', '==', customerId),
    where('date', '>=', Timestamp.fromDate(date))
  );
  const snapshot = await getDocs(appointmentsQuery);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

export async function checkSlotAvailability(coachId: string, slot: Date) {
  const existingBookingQuery = query(
    collection(db, 'appointments'),
    where('coachId', '==', coachId),
    where('date', '==', Timestamp.fromDate(slot))
  );
  const snapshot = await getDocs(existingBookingQuery);
  return snapshot.empty;
}

export async function isWithinScheduledAvailability(coachId: string, slot: Date): Promise<boolean> {
  try {
    // Get all approved availabilities for the coach
    const availabilitiesQuery = query(
      collection(db, 'availabilities'),
      where('coachId', '==', coachId),
      where('status', '==', 'approved')
    );
    const snapshot = await getDocs(availabilitiesQuery);
    
    if (snapshot.empty) {
      return false; // No approved availabilities
    }
    
    const availabilities = snapshot.docs.map(doc => doc.data());
    
    // Check if the slot falls within any approved availability
    for (const availability of availabilities) {
      const startDate = availability.startDate.toDate();
      const endDate = availability.endDate.toDate();
      
      // Check if slot is within the date range
      if (slot >= startDate && slot <= endDate) {
        // Check if slot is within the scheduled days and times
        const dayOfWeek = slot.getDay().toString();
        const selectedDays = availability.selectedDays || {};
        const selectedSessions = selectedDays[dayOfWeek] || [];
        
        if (selectedSessions.length === 0) {
          continue; // No sessions for this day
        }
        
        // Check if slot is within any of the scheduled sessions
        const SESSION_TIMES = {
          session1: { start: { hours: 17, minutes: 0 }, end: { hours: 20, minutes: 0 } },
          session2: { start: { hours: 20, minutes: 0 }, end: { hours: 22, minutes: 0 } }
        };
        
        for (const session of selectedSessions) {
          const sessionTimes = SESSION_TIMES[session as keyof typeof SESSION_TIMES];
          if (!sessionTimes) continue;
          
          const slotHour = slot.getHours();
          const slotMinute = slot.getMinutes();
          const slotTimeInMinutes = slotHour * 60 + slotMinute;
          
          const sessionStartInMinutes = sessionTimes.start.hours * 60 + sessionTimes.start.minutes;
          const sessionEndInMinutes = sessionTimes.end.hours * 60 + sessionTimes.end.minutes;
          
          if (slotTimeInMinutes >= sessionStartInMinutes && slotTimeInMinutes < sessionEndInMinutes) {
            return true; // Slot is within scheduled availability
          }
        }
      }
    }
    
    return false; // Slot is not within any scheduled availability
  } catch (error) {
    console.error('Error checking scheduled availability:', error);
    return false;
  }
}

export async function createAppointment(
  customerId: string,
  coachId: string,
  slot: Date,
  customerData?: AppointmentCustomerSource | null
) {
  // Check if the slot is within scheduled availability times
  const isWithinSchedule = await isWithinScheduledAvailability(coachId, slot);
  
  if (!isWithinSchedule) {
    throw new Error('Appointments can only be booked during scheduled availability times');
  }
  
  return await addDoc(collection(db, 'appointments'), {
    customerId,
    coachId,
    // Names (Ar/En) and phone so the coach sees who booked (namat#36 P1-9).
    ...appointmentCustomerFields(customerData),
    date: Timestamp.fromDate(slot),
    status: 'scheduled',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
}